import React, { useMemo, useState } from "react";

import { useScopedBoxes } from "../../../hooks/useScopedBoxes";
import { fmtWorkCenter } from "../../../lib/format";

/** Box visibility editor used inside the edit modal. */
export const VisibilityEditor: React.FC<{
  mode: "derived" | "explicit";
  setMode: (m: "derived" | "explicit") => void;
  grants: Map<string, { read: boolean; write: boolean }>;
  setGrants: React.Dispatch<React.SetStateAction<Map<string, { read: boolean; write: boolean }>>>;
}> = ({ mode, setMode, grants, setGrants }) => {
  const { snapshot } = useScopedBoxes();
  const [filter, setFilter] = useState("");
  const boxes = useMemo(() => {
    const all = [...(snapshot?.boxes ?? [])].sort((a, b) =>
      (a.smartbox_code ?? a.scu_name).localeCompare(b.smartbox_code ?? b.scu_name),
    );
    const q = filter.trim().toLowerCase();
    return q
      ? all.filter((b) =>
          [b.smartbox_code, b.scu_name, b.work_center].some((v) => (v ?? "").toLowerCase().includes(q)),
        )
      : all;
  }, [snapshot, filter]);

  const toggle = (scu: string, field: "read" | "write"): void =>
    setGrants((prev) => {
      const next = new Map(prev);
      const cur = next.get(scu) ?? { read: false, write: false };
      const upd = { ...cur, [field]: !cur[field] };
      if (field === "read" && !upd.read) upd.write = false; // no write without read
      if (field === "write" && upd.write) upd.read = true;
      if (!upd.read && !upd.write) next.delete(scu);
      else next.set(scu, upd);
      return next;
    });

  return (
    <div>
      <div className="flex gap-4 mb-2 text-sm">
        <label className="flex items-center gap-1.5">
          <input type="radio" checked={mode === "derived"} onChange={() => setMode("derived")} />
          Company default
        </label>
        <label className="flex items-center gap-1.5">
          <input type="radio" checked={mode === "explicit"} onChange={() => setMode("explicit")} />
          Explicit list
        </label>
      </div>
      {mode === "explicit" && (
        <>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="filter boxes…"
            className="border border-secondary-300 rounded px-2 py-1 text-xs mb-1 w-full"
          />
          <div className="max-h-56 overflow-auto border border-secondary-200 rounded divide-y divide-secondary-100">
            {boxes.map((b) => {
              const g = grants.get(b.scu_name);
              return (
                <div key={b.scu_name} className="flex items-center justify-between px-3 py-1.5 text-sm">
                  <span>
                    {b.smartbox_code ?? b.scu_name}
                    {b.work_center && <span className="text-secondary-500"> · {fmtWorkCenter(b.work_center)}</span>}
                  </span>
                  <span className="flex items-center gap-3 text-xs">
                    <label className="flex items-center gap-1">
                      <input type="checkbox" checked={!!g?.read} onChange={() => toggle(b.scu_name, "read")} />
                      see
                    </label>
                    <label className="flex items-center gap-1 text-secondary-500">
                      <input type="checkbox" checked={!!g?.write} onChange={() => toggle(b.scu_name, "write")} />
                      write
                    </label>
                  </span>
                </div>
              );
            })}
            {boxes.length === 0 && (
              <div className="px-3 py-3 text-xs text-secondary-400">No boxes.</div>
            )}
          </div>
          <p className="text-[11px] text-secondary-500 mt-1">
            {grants.size} box{grants.size === 1 ? "" : "es"} selected — this user will see exactly these.
          </p>
        </>
      )}
    </div>
  );
};
